import React from 'react';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CaptainSelectionStep = ({ formData, errors, onChange }) => {
  // Helper function to get player name
  const getPlayerName = (player) => {
    if (player?.name) {
      return player.name;
    }
    // Construct name from firstName, middleName, lastName
    const parts = [player?.firstName, player?.middleName, player?.lastName].filter(Boolean);
    return parts.join(' ').trim();
  };

  // Filter players that have at least a first name and last name (required fields)
  const validPlayers = formData?.players?.filter(player => {
    const name = getPlayerName(player);
    return name && name.trim() !== '' && player?.firstName && player?.lastName;
  }) || [];

  const captainOptions = validPlayers.map(player => {
    const name = getPlayerName(player);
    return {
      value: player?.id?.toString() || player?.dbId?.toString(),
      label: name,
      description: `${player?.role ? player.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Role not specified'} - ${player?.mobile || 'No mobile'}`
    };
  });

  return (
    <div className="space-y-6">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-semibold">4</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Captain Selection</h3>
            <p className="text-sm text-muted-foreground">
              Choose the team captain from registered players
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 space-y-6">
        {captainOptions?.length > 0 ? (
          <>
            <Select
              label="Team Captain"
              description="Select the player who will lead the team"
              placeholder="Choose captain from registered players"
              options={captainOptions}
              value={formData?.captainId}
              onChange={(value) => onChange('captainId', value)}
              error={errors?.captainId}
              searchable
              required
            />

            {formData?.captainId && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <div className="flex gap-3">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  <div>
                    <p className="text-sm font-medium text-success-foreground">Captain Selected</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {captainOptions?.find(opt => opt?.value === formData?.captainId)?.label} has been designated as team captain.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <div>
                <p className="text-sm font-medium text-warning-foreground">No Players Available</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Please complete player registration in the previous step before selecting a captain.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-muted/50 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <Icon name="Info" size={16} />
            Captain Responsibilities
          </h4>
          <ul className="space-y-2 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Lead the team during matches and represent the team at toss</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Coordinate with coaches and tournament officials</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Ensure team discipline and adherence to tournament rules</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Act as primary point of contact for on-field decisions</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CaptainSelectionStep;